import { chain, range } from 'lodash';

/**
 * The cron object is not written to the model itself. Instead, it is used as an intermediary
 * object to eventually generate a cron tab value (e.g. '0 00 22 1 12 *') that is written to the
 * `schedule` field of the command.
 */
export const getDefaultCron = () => ({
  seconds: [{ value: 0, repeatEvery: null }],
  minutes: [{ value: null, repeatEvery: null }],
  hours: [{ value: null, repeatEvery: null }],
  days: [{ value: null, repeatEvery: null }],
  months: [{ value: null, repeatEvery: null }],
  weekdays: {
    Sunday: false,
    Monday: false,
    Tuesday: false,
    Wednesday: false,
    Thursday: false,
    Friday: false,
    Saturday: false,
  },
});

export default {
  /**
   * Generates a string from the intermediary cron object. The string may be written to the
   * `schedule` field of the command model.
   * @param {Object} cron - The cron object. See `getDefaultCron` for the object structure
   * @return {String} The raw cron tab value
   */
  getCronString(cron) {
    const generateCronEntry = entry => (
      entry.map((value) => {
        let ret = '';
        if (value.repeatEvery !== null) {
          ret = `/${value.repeatEvery}`;
        }
        if (value.value === null) {
          ret = `*${ret}`;
        } else {
          ret = `${value.value}${ret}`;
        }
        return ret;
      }).join(',')
    );

    const generateWeekdayCronEntry = (entry) => {
      const result = [];
      if (entry.Sunday) result.push(0);
      if (entry.Monday) result.push(1);
      if (entry.Tuesday) result.push(2);
      if (entry.Wednesday) result.push(3);
      if (entry.Thursday) result.push(4);
      if (entry.Friday) result.push(5);
      if (entry.Saturday) result.push(6);
      if (!result.length) result.push('*');
      return result.join(',');
    };

    const seconds = generateCronEntry(cron.seconds);
    const minutes = generateCronEntry(cron.minutes);
    const hours = generateCronEntry(cron.hours);
    const days = generateCronEntry(cron.days);
    const months = generateCronEntry(cron.months);
    const weekdays = generateWeekdayCronEntry(cron.weekdays);
    return `${seconds} ${minutes} ${hours} ${days} ${months} ${weekdays}`;
  },

  /**
   * Transform a raw cron tab value (e.g. '0 00 22 1 12 *') to the more easily manipulated
   * "cron" object.
   * @param {String} cronTab - The raw cron tab value
   * @return {Object} The cron object. See `getDefaultCron` for the object structure
   */
  parseCronTab(cronTab) {
    if (typeof cronTab !== 'string') return getDefaultCron();

    const cronTabSplit = cronTab.split(' ');
    if (cronTabSplit.length !== 6) return getDefaultCron();

    const readCronValue = (value) => {
      if (value === '*' || value === null || value === '') return null;
      return parseInt(value, 10);
    };

    const parseCronRanges = (value) => {
      if (value.indexOf('-') !== -1) {
        return range(parseInt(value[0], 10), parseInt(value[1], 10) + 1);
      }

      const parsedInt = parseInt(value, 10);
      return parsedInt ? [parsedInt] : value;
    };

    const parseCronValues = (cronString) => {
      let repeatEvery = null;
      let value = cronString;
      if (value.indexOf('/') !== -1) {
        [value, repeatEvery] = value.split('/');
      }

      let parsedValue = parseCronRanges(value);
      parsedValue = chain(parsedValue).flatten().uniq().value();

      return parsedValue.map((pValue) => {
        const cronValue = readCronValue(pValue);
        const pRepeatEvery = readCronValue(repeatEvery);

        // If there is a repeat value, only return values that are valid.
        if (pRepeatEvery) {
          if (!cronValue) {
            return { value: cronValue, repeatEvery: readCronValue(repeatEvery) };
          }
          if (pValue % pRepeatEvery === 0) {
            // This hits for things like get even days between range
            return { value: cronValue, repeatEvery: null };
          }
        }
        return { value: cronValue, repeatEvery: null };
      });
    };

    const parseCronEntry = (entry) => {
      if (!entry || entry === 'undefined') return [{ value: null, repeatEvery: null }];

      const parsedEntry = entry.split(',').map(v => parseCronValues(v));
      return chain(parsedEntry).flatten().compact().value();
    };

    const parseCronWeekdays = (rawEntry) => {
      const entry = parseCronEntry(rawEntry).map(d => d.value);
      return {
        Sunday: entry.includes(0),
        Monday: entry.includes(1),
        Tuesday: entry.includes(2),
        Wednesday: entry.includes(3),
        Thursday: entry.includes(4),
        Friday: entry.includes(5),
        Saturday: entry.includes(6),
      };
    };

    return {
      seconds: parseCronEntry(cronTabSplit[0]),
      minutes: parseCronEntry(cronTabSplit[1]),
      hours: parseCronEntry(cronTabSplit[2]),
      days: parseCronEntry(cronTabSplit[3]),
      months: parseCronEntry(cronTabSplit[4]),
      weekdays: parseCronWeekdays(cronTabSplit[5]),
    };
  },
};
